import moment from 'moment-timezone';

import { fieldTypes } from '../../constants/metainf';
import { AGGREGATIONS_CSV, COLUMNS_CSV, PARAM_CSV } from '../../utils/csv';
import { DATE_DAILY_SHORT_FORMAT } from '../../constants/date';
import {
  getParamEventsHeader,
  getParamEventsHeaderWithAdditional,
  getRetentionHeader,
} from './headerComposer';
import csvRowsPrinter, { csvRowsPrinterWithAdditional, toDateFormat } from './csvRowsPrinter';
import { groupBySplitWithDatesCSV } from '../../chart/period/PeriodChartDataConverter';
import { splitPostfix } from '../../constants/event';

const columns = {
  groupBy: (field) => field,
  groupByDay: (field) => field,
  groupByMs: (field) => COLUMNS_CSV.MS + field,
  splitBy: (field) => field,
  splitByDay: (field) => field,
  splitByMs: (field) => COLUMNS_CSV.MS + field,
  count: () => COLUMNS_CSV.COUNT,
  sum: (_, query) =>
    query.aggregations.find(
      ({ type }) =>
        type === AGGREGATIONS_CSV.AVERAGE_TOTAL || type === AGGREGATIONS_CSV.AVERAGE_TOTAL_SPLIT
    )
      ? COLUMNS_CSV.AVG_TOTAL
      : COLUMNS_CSV.TOTAL,
  max: () => COLUMNS_CSV.MAX,
  min: () => COLUMNS_CSV.MIN,
  avg: () => COLUMNS_CSV.AVG,
};

// возвращает названия колонок для данных csv
const getColumns = ({ groupBy, splitBy, query }, csvParams) =>
  Object.entries(csvParams).map(([key]) => {
    let field;
    if (key.includes(PARAM_CSV.GROUP_BY)) {
      field = groupBy;
    } else if (key.includes(PARAM_CSV.SPLIT_BY)) {
      field = splitBy;
    }

    return columns[key](field, query);
  });

// возвращает названия колонок для данных csv
const getColumnsWithOthers = ({ groupBy, splitBy, query }, csvParams) =>
  Object.entries(csvParams).map(([key]) => {
    let field;
    if (key.includes(PARAM_CSV.GROUP_BY)) {
      field = groupBy;
    } else if (key.includes(PARAM_CSV.SPLIT_BY)) {
      field = groupBy === splitBy ? `${splitBy}${splitPostfix}` : splitBy;
    }

    return columns[key](field, query);
  });

// необходимо знать type для group by и split by, чтобы в дальнейшем знать в каком формате их выводить.
const getTypeColumns = (info, getType) =>
  Object.fromEntries(
    Object.entries({
      [info.groupBy]: getType(info.groupBy).type,
      [info.splitBy]: getType(info.splitBy).type,
    }).filter(([, value]) => value)
  );

// фильтрует csv параметры исходя из полученных данных
const filterCsvParams = (csvParams, { query, splitBy }, getType) =>
  Object.entries(csvParams).reduce((res, [key, val]) => {
    const typeSplit = getType(splitBy).type;
    let isSkip = false;
    const splitDate = [fieldTypes.TIME, fieldTypes.COHORT].includes(typeSplit);
    if (
      [PARAM_CSV.SUM, PARAM_CSV.AVG_SUM, PARAM_CSV.MAX, PARAM_CSV.MIN, PARAM_CSV.AVG].includes(
        key
      ) &&
      !query.aggregations.find(({ type }) => type.includes(AGGREGATIONS_CSV.TOTAL))
    ) {
      isSkip = true;
    }

    if (
      key === PARAM_CSV.AVG &&
      query.aggregations.find(
        ({ type }) =>
          type === AGGREGATIONS_CSV.AVERAGE_TOTAL || type === AGGREGATIONS_CSV.AVERAGE_TOTAL_SPLIT
      )
    ) {
      isSkip = true;
    }
    if (key === PARAM_CSV.SPLIT_BY && splitDate) {
      isSkip = true;
    }
    if ((key === PARAM_CSV.SPLIT_BY_DAY || key === PARAM_CSV.SPLIT_BY_MS) && !splitDate) {
      isSkip = true;
    }
    if (key.includes(PARAM_CSV.SPLIT_BY) && !splitBy) {
      isSkip = true;
    }
    if (key.includes(PARAM_CSV.HEADER) || !val) {
      isSkip = true;
    }
    if (key.includes(PARAM_CSV.PERIODS)) {
      isSkip = true;
    }
    return isSkip ? res : { ...res, [key]: val };
  }, {});

// используется для параметризированной загрузки csv(данные отображаются соответсвенно выбранным параметрам)
export const toCsvEvents = ({ info, result }, getType, csvParams = {}) => {
  const filteredCsvParams = filterCsvParams(csvParams, info, getType);
  const getedColumns = getColumns(info, filteredCsvParams);

  const header = csvParams.additionalHeader ? [getParamEventsHeader(info)] : [];

  return header
    .concat([
      getedColumns.join(';'),
      csvRowsPrinter(result.rows, getedColumns, getTypeColumns(info, getType), info),
    ])
    .join('\n');
};

const getAdditionalPeriods = (additionalEvents) => {
  const additionalPeriods = additionalEvents.map((additionalEvent) => {
    const dateFromAdditional = moment
      .tz(additionalEvent.info.query.globalFilter.from, additionalEvent.info.timezone)
      .format(DATE_DAILY_SHORT_FORMAT);

    const dateToAdditional = moment
      .tz(additionalEvent.info.query.globalFilter.to, additionalEvent.info.timezone)
      .subtract(1, 'days')
      .format(DATE_DAILY_SHORT_FORMAT);

    return {
      from: dateFromAdditional,
      to: dateToAdditional,
      uniqueChar: additionalEvent.info.uniqueChar,
    };
  });

  return additionalPeriods;
};

const groupByAttributes = (allEntriesAttributes, rows, info, emptyRow) => {
  return allEntriesAttributes.map((attribute) => {
    const existedAttribute = rows.find((row) => row[info.groupBy] === attribute);
    if (!existedAttribute) {
      return {
        ...emptyRow,
        [info.groupBy]: attribute,
      };
    }

    return existedAttribute;
  });
};

const getAllEventsAttributes = (events) => {
  const eventsAttributes = events.map((event) =>
    event.result.rows.map((row) => row[event.info.groupBy])
  );

  const allEventAttributes = eventsAttributes.reduce(
    (res, eventAttributes) => res.concat(eventAttributes),
    []
  );

  const allEntriesAttributes = Array.from(new Set(allEventAttributes));

  return allEntriesAttributes;
};

// получение результирующих строк таблицы CSV
const getResultedRows = (
  dataCSVResult,
  additionalDataCSVResult,
  event,
  isDate,
  timeAggregationCounts
) => {
  let resultedRows = [];
  const combinedResult = [dataCSVResult].concat(additionalDataCSVResult);

  // находим данные с наибольшим количеством строк и берем это количество
  const maxRowsCount = combinedResult.reduce(
    (maxCount, result) => Math.max(maxCount, result.length),
    0
  );

  // пустая строка-заглушка таблицы
  const emptyCSVRow = new Array(dataCSVResult[0]?.length).fill('');

  // итерируемся по числу наибольшего количества строк периода
  for (let i = 0; i < maxRowsCount; i++) {
    // комбинируем данные двух периодов в одну строку, при отсутствии данных по одному из периодов, вставляем заглушку emptyCSVRow
    const combinedEvents = combinedResult.map((res) => res[i] || emptyCSVRow).join(';');

    // если группировка по датам, отображаем данную группировку в начале каждой строки таблицы
    const preparedCombinedRow = isDate
      ? `${event.info.groupBy} ${timeAggregationCounts[i]};${combinedEvents}`
      : `${combinedEvents}`;
    resultedRows = [...resultedRows, preparedCombinedRow];
  }
  return resultedRows;
};

// подготовка данных: группировка по аттрибутам, удаление ненужных строк, вставка строк заглушек
const getPreparedEventsData = (events, isDate) => {
  const [event] = events;
  const groupedRows = events.map(({ info, result }) => {
    return isDate
      ? groupBySplitWithDatesCSV(result.rows, info)
      : groupByAttributes(getAllEventsAttributes(events), result.rows, info);
  });

  // находим данные с наибольшим количеством строк и берем это количество
  const maxRowsCount = groupedRows.reduce(
    (maxCount, result) => Math.max(maxCount, result.length),
    0
  );

  const preparedEventsData = new Array(groupedRows.length).fill([]);

  for (let i = 0; i < maxRowsCount; i++) {
    // если в одной точке нет данных по всем периодам, то не отображаем эту точку
    const isEmptyEvents = groupedRows.every((res) => {
      const aggregation = event.info.aggregation.find((aggregationItem) => {
        if (res[i]) {
          return Object.keys(res[i]).includes(aggregationItem);
        }
        return undefined;
      });
      return isDate && res[i] && !res[i][aggregation];
    });
    if (!isEmptyEvents) {
      groupedRows.forEach((res, index) => {
        if (res[i]) {
          preparedEventsData[index] = [
            ...preparedEventsData[index],
            { ...res[i], timeAggregationCount: i + 1 },
          ];
        }
      });
    }
  }

  return preparedEventsData;
};

export const toCsvEventsWithAdditional = (events, getType, csvParams = {}) => {
  const [event, ...additionalEvents] = events;
  const additionalPeriods = getAdditionalPeriods(additionalEvents);

  const header = csvParams.additionalHeader
    ? [getParamEventsHeaderWithAdditional(event.info, additionalPeriods)]
    : [];

  const isDate = event.info.timeUnit !== undefined;

  const filteredCsvParams = filterCsvParams(csvParams, event.info, getType);
  const gottenColumns = getColumns(event.info, filteredCsvParams);

  const preparedEventsData = getPreparedEventsData(events, isDate);
  const timeAggregationCounts = preparedEventsData
    .reduce((acc, res) => (acc.length > res.length ? acc : res))
    .map(({ timeAggregationCount }) => timeAggregationCount || 0);

  const [eventResult, ...additionalEventsResult] = events.map(({ info }, index) => {
    const gottenColumnsVar = [...gottenColumns];

    if (index !== 0 && !isDate) {
      const indexColumnGroupBy = gottenColumnsVar.findIndex((col) => col === info.groupBy);
      if (indexColumnGroupBy !== -1) {
        gottenColumnsVar.splice(indexColumnGroupBy, 1);
      }
    }

    return [
      gottenColumnsVar
        .map((column, idx) =>
          idx === 0 && index === 0 && !isDate ? `${column}` : `${column}(${info.uniqueChar})`
        )
        .join(';'),
      csvRowsPrinterWithAdditional(
        preparedEventsData[index],
        gottenColumnsVar,
        getTypeColumns(info, getType),
        info
      ),
    ];
  });

  const [columnsCSVResult, dataCSVResult] = eventResult;

  const additionalColumnsCSVResult = additionalEventsResult
    .map(([additionalColumns]) => additionalColumns)
    .join(';');

  const additionalDataCSVResult = additionalEventsResult.map(
    ([, additionalResult]) => additionalResult
  );

  const columnsResult = isDate
    ? `TYPE,${columnsCSVResult};${additionalColumnsCSVResult}`
    : `${columnsCSVResult};${additionalColumnsCSVResult}`;

  const resultedRows = getResultedRows(
    dataCSVResult,
    additionalDataCSVResult,
    event,
    isDate,
    timeAggregationCounts
  );

  return [header].concat([columnsResult, resultedRows.join('\n')]).join('\n');
};

const sortRowsByGroup = (rows, groupBy) => {
  const rowsCopy = [...rows];
  // сортировка осуществляется по groupBy
  // в начало массива идут отсортированные строки таблицы по числовым элементам, затем по строковым
  return rowsCopy.sort((row, nextRow) => {
    const sortItem = Number.isNaN(Number(`${row[groupBy]}`)) ? row[groupBy] : Number(row[groupBy]);
    const nextSortItem = Number.isNaN(Number(`${nextRow[groupBy]}`))
      ? nextRow[groupBy]
      : Number(nextRow[groupBy]);

    if (
      sortItem > nextSortItem ||
      (typeof sortItem === 'string' && typeof nextSortItem === 'number')
    ) {
      return 1;
    }
    if (
      sortItem < nextSortItem ||
      (typeof sortItem === 'number' && typeof nextSortItem === 'string')
    ) {
      return -1;
    }
    return 0;
  });
};

const createEmptyRow = (notEmptyEvent) => {
  const emptyRow = {};

  if (notEmptyEvent) {
    Object.keys(notEmptyEvent.result.rows[0]).forEach((key) => {
      emptyRow[key] = '';
    });
    emptyRow[notEmptyEvent.info.groupBy] = notEmptyEvent[notEmptyEvent.info.groupBy];
  }

  return emptyRow;
};

// трансформация строк результата в зависимости от наличия splitBy
const getPreparedRows = (events, splitBy, getType) => {
  const [event] = events;
  const { groupBy } = event.info;
  const splitByVar = groupBy === splitBy ? `${splitBy}${splitPostfix}` : splitBy;
  const typeColumns = getTypeColumns(event.info, getType);
  const type = typeColumns[groupBy];
  const getResultedValueFormat = (value) => {
    return [fieldTypes.TIME, fieldTypes.COHORT, fieldTypes.DATE].includes(type)
      ? toDateFormat(groupBy, value, event.info.timezone)
      : value;
  };
  // достаем все groupBy, которые есть по двум запросам
  const allEventsAttributes = getAllEventsAttributes(events);
  const notEmptyEvent = events.find((eventVar) => eventVar.result.rows.length !== 0);
  const emptyRow = createEmptyRow(notEmptyEvent);

  const groupedSortedRows = events.map(({ info, result }) => {
    if (splitBy) {
      const sortedRows = sortRowsByGroup(result.rows, groupBy);
      const groupedRows = sortedRows.map((row) => ({
        ...row,
        [splitByVar]: row[splitByVar] !== undefined ? row[splitByVar] : 'others',
      }));
      return groupedRows;
    }

    const groupedRows = groupByAttributes(allEventsAttributes, result.rows, info, emptyRow);
    return sortRowsByGroup(groupedRows, groupBy);
  });

  let preparedRows = [];
  // при наличии сплита данные с others кладутся после данных основного запроса по каждому groupBy в один массив
  if (splitBy) {
    const [dataCSV, othersDataCSV] = groupedSortedRows;
    preparedRows = [...dataCSV];

    othersDataCSV.forEach((row) => {
      const existingAggregateIndex = preparedRows.findIndex((resultedRow, index) => {
        const isLastRow = preparedRows.length === index + 1;
        const resultValue = getResultedValueFormat(resultedRow[groupBy]);
        const currentValue = getResultedValueFormat(row[groupBy]);
        const nextResultValue = isLastRow
          ? undefined
          : getResultedValueFormat(preparedRows[index + 1][groupBy]);

        return resultValue === currentValue && (isLastRow || nextResultValue !== currentValue);
      });

      if (existingAggregateIndex !== -1) {
        preparedRows.splice(existingAggregateIndex + 1, 0, row);
      } else {
        preparedRows.push(row);
      }
    });
  } else {
    preparedRows = [...groupedSortedRows];
  }

  return preparedRows;
};

const getCSVEventsWithSplit = (header, gottenColumns, preparedEvents, event, getType, splitBy) => {
  return [header]
    .concat([
      gottenColumns.join(';'),
      csvRowsPrinterWithAdditional(
        preparedEvents,
        gottenColumns,
        getTypeColumns(event.info, getType),
        {
          ...event.info,
          splitBy,
        }
      ).join('\n'),
    ])
    .join('\n');
};

// при отсутствии сплита данные с others кладутся в отдельные колонки таблицы после данных основного запроса по соответствующим groupBy
const getCSVEventsWithoutSplit = (events, header, gottenColumns, preparedEvents, getType) => {
  const [eventResult, othersEventResult] = events.map(({ info }, index) => {
    const isOthersEvent = index === 1;
    const gottenColumnsVar = [...gottenColumns];

    if (isOthersEvent) {
      // в таблице мы один раз отображаем groupBy, поэтому убираем соответствующую колонку в запросе с others
      const indexColumnGroupBy = gottenColumnsVar.findIndex((col) => col === info.groupBy);
      if (indexColumnGroupBy !== -1) {
        gottenColumnsVar.splice(indexColumnGroupBy, 1);
      }
    }

    return [
      gottenColumnsVar
        .map((column, idx) =>
          idx === 0 && index === 0 ? `${column}` : `${column}(${info.uniqueChar})`
        )
        .join(';'),
      csvRowsPrinterWithAdditional(
        preparedEvents[index],
        gottenColumnsVar,
        getTypeColumns(info, getType),
        info
      ),
    ];
  });

  const [columnsCSVResult, dataCSVResult] = eventResult;
  const [othersColumnsCSVResult, othersDataCSVResult] = othersEventResult;

  const columnsResult = `${columnsCSVResult};${othersColumnsCSVResult}`;

  // конкатенация данных из двух запросов по одинаковому groupBy
  const resultedRows = dataCSVResult.reduce((result, row, index) => {
    const combinedEvents = row.concat(othersDataCSVResult[index]);
    return [...result, combinedEvents];
  }, []);

  return [header].concat([columnsResult, resultedRows.join('\n')]).join('\n');
};

export const toCsvEventsWithOthers = (events, getType, csvParams = {}) => {
  const [event] = events;
  const { splitBy } = event.info;

  const header = csvParams.additionalHeader ? [getParamEventsHeader(event.info)] : [];

  const filteredCsvParams = filterCsvParams(csvParams, event.info, getType);
  const gottenColumns = getColumnsWithOthers(event.info, filteredCsvParams);

  // трансформация данных результатов в зависимости от наличия splitBy
  const preparedRows = getPreparedRows(events, splitBy, getType);

  if (splitBy) {
    return getCSVEventsWithSplit(header, gottenColumns, preparedRows, event, getType, splitBy);
  }

  return getCSVEventsWithoutSplit(events, header, gottenColumns, preparedRows, getType);
};

// используется на вкладке users, входные данные являются сконвертированными для таблицы(там есть определенный
// порядок колонок).
export const toCsvUsers = ({ columns: columnsParam, rows }) => {
  const header = columnsParam.map(({ Header }) => Header).join(';');

  const prepareRow = (row) => columnsParam.map(({ accessor }) => accessor(row)).join(';');

  return [header].concat(rows.map((item) => prepareRow(item))).join('\n');
};

// используется для retention (тут используется специфический конвертер не похожий на конвертер данных ивентов).
export const toCsvRetention = ({ retentionUnits }, query, timezone) => {
  const toDate = (date) => moment.tz(Number(date), timezone).format(DATE_DAILY_SHORT_FORMAT);
  const header = getRetentionHeader(query);

  return [header]
    .concat(
      Object.entries(retentionUnits)
        .filter(([, units]) => units[0])
        .map(([date, units]) => `${toDate(date)};${units.map((unit) => unit).join(';')}`)
    )
    .join('\n');
};

/**
 * используется для retention, usage, revenue.
 * @param result конвертированный ответ от сервера с данными.
 * @param info данные из конвертации ответа.
 * @param columns колонки которые выводятся в сsv.
 * @param getType функция которая определяет тип для group by и split by.
 * @returns {string} данные csv файла.
 */
export const toCsvEvent = ({ result, info }, columnsParam, getType) => {
  const { rows } = result;
  const header = columnsParam.map((column) => column).join(';');
  const content = csvRowsPrinter(rows, columnsParam, getTypeColumns(info, getType), info);
  return [header, content].join('\n');
};
